import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumns } from '@mui/x-data-grid';
import { getStringEqualsOperator } from '../../filterOperators/stringEqualsOperator';
import { getStringDiffersOperator } from '../../filterOperators/stringDiffersOperator';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../../filterOperators/dateFilterOperators';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { EInvitationStatus, IBasicInvitationDetails } from '../../resources/models/IInvitationDetails';
import { IPage } from '../../resources/models/IPage';
import { getEnumIsFilterOperator, getEnumIsNotFilterOperator } from '../../filterOperators/enumFilterOperator';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { useResourceChecker } from '../../resources/resource';
import Button from '@mui/material/Button';
import { Link, useHistory } from 'react-router-dom';
import { queryUrl } from '../../hooks/useQueryUrl';
import { RowButtonsContainer } from '../RowButtonsContainer/RowButtonsContainer';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { NotificationContext } from '../../contexts/NotificationContext';

interface IProps {
  data: IPage<IBasicInvitationDetails>;
  onRefresh: () => Promise<any>;
}

const StatusChip = createStatusChipComponent({
  [EInvitationStatus.Rejected]: ['invites.status.rejected', 'red'],
  [EInvitationStatus.Accepted]: ['invites.status.accepted', 'lime'],
  [EInvitationStatus.Pending]: ['invites.status.pending', 'default'],
  [EInvitationStatus.Expired]: ['invites.status.expired', 'orange'],
});

const { useCancelInvitation } = apiOrganization;

export const InvitesTable: FC<IProps> = ({ data, onRefresh }) => {
  const { t } = useTranslation();
  const { addNotification } = useContext(NotificationContext);

  const { organization } = useContext(OrganizationContext);

  const { resource } = useCancelInvitation(organization?.name, 'any');
  const checker = useResourceChecker();
  const history = useHistory();

  const isCancelInvitationAllowed = checker(resource);

  const inviteId = useQueryParam('cancel');
  const invite = data.data.find(({ id }) => id === inviteId);
  const closeDialog = useCloseDialog(void 0, ['cancel']);
  const handleError = useApiErrorHandler();

  const { execute: cancelInvite } = useCancelInvitation(organization?.name, invite?.id);

  const onCancelInvite = useCallback(async () => {
    try {
      await cancelInvite();
      addNotification({
        severity: 'success',
        message: t('invites.cancel.success'),
      });
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [t, closeDialog, handleError]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'email',
        headerName: t('common.tableHeader.email'),
        filterOperators: [getStringEqualsOperator(), getStringDiffersOperator()],
        flex: 2,
      },
      {
        field: 'status',
        headerName: t('organization.invites.table.status'),
        filterOperators: [
          getEnumIsFilterOperator({
            [t('invites.status.accepted')]: EInvitationStatus.Accepted,
            [t('invites.status.pending')]: EInvitationStatus.Pending,
            [t('invites.status.rejected')]: EInvitationStatus.Rejected,
            [t('invites.status.expired')]: EInvitationStatus.Expired,
          }),
          getEnumIsNotFilterOperator({
            [t('invites.status.accepted')]: EInvitationStatus.Accepted,
            [t('invites.status.pending')]: EInvitationStatus.Pending,
            [t('invites.status.rejected')]: EInvitationStatus.Rejected,
            [t('invites.status.expired')]: EInvitationStatus.Expired,
          }),
        ],
        renderCell: StatusChip,
        flex: 1,
      },
      {
        field: 'created_at',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
        width: 130,
      },
      {
        field: 'updated_at',
        headerName: t('common.tableHeader.updated'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
        width: 130,
      },
      {
        field: 'buttons',
        headerName: ' ',
        hide: !isCancelInvitationAllowed,
        filterable: false,
        sortable: false,
        hideable: !isCancelInvitationAllowed,
        disableColumnMenu: true,
        width: 140,
        renderCell: (params) => {
          if (params.row.status !== EInvitationStatus.Pending) return '';

          const href = queryUrl('cancel', params.row.id, history.location.search);

          return (
            <RowButtonsContainer>
              <Button variant={'outlined'} color={'primary'} component={Link} to={href} replace={true}>
                {t('invites.cancel.button')}
              </Button>
            </RowButtonsContainer>
          );
        },
      },
    ],
    [t, isCancelInvitationAllowed]
  );

  return (
    <>
      <UrlQueryDataGrid
        columns={columns}
        rows={data.data}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        paginationMode={'server'}
        filterMode={'server'}
        sortingMode={'server'}
        rowCount={data.total_items}
      />
      <ConfirmationDialog
        open={!!invite}
        title={t('invites.cancel.confirm.title')}
        message={t('invites.cancel.confirm.message')}
        confirm={onCancelInvite}
        abort={closeDialog}
      />
    </>
  );
};
